// Shared helper for the gallery asset-mark endpoint (like / dislike / approved).
// Centralizes the POST so every owner (gallery container, session workbench) hits
// /api/organization/generations/mark identically. Returns true on success.
export type AssetMark = 'like' | 'dislike' | 'approved';

interface PostGenerationMarkArgs {
  token: string | null;
  generationId: number;
  organizationId: number;
  mark: AssetMark | null;
}

export async function postGenerationMark({
  token,
  generationId,
  organizationId,
  mark,
}: PostGenerationMarkArgs): Promise<boolean> {
  const response = await fetch('/api/organization/generations/mark', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      generationId,
      organization_id: organizationId,
      mark,
    }),
  });
  return response.ok;
}

/**
 * Per-asset write serializer. Out-of-order *failures* are handled by a seq guard
 * in the owners, but out-of-order *successes* are not: clicking like→dislike can
 * race so the server processes the older `like` last, leaving the server on `like`
 * while the UI shows `dislike` (diverges on reload / CSV export). Chaining each
 * asset's POSTs so a later write only fires after the previous one settles makes
 * the server's write order match the click order, so last-writer-wins matches the
 * last click. Optimistic UI updates still happen synchronously at click time —
 * only the network call is queued.
 */
export function createMarkSerializer() {
  const tails = new Map<number, Promise<void>>();
  return function enqueue<T>(id: number, task: () => Promise<T>): Promise<T> {
    const prev = tails.get(id) ?? Promise.resolve();
    // Run task after the previous write settles, regardless of its outcome.
    const result = prev.then(task, task);
    // Swallow the result so the chain never rejects the next task; track the tail.
    const tail = result.then(() => {}, () => {});
    tails.set(id, tail);
    // Drop the map entry once this is the last settled write for the id (no leak).
    void tail.then(() => {
      if (tails.get(id) === tail) tails.delete(id);
    });
    return result;
  };
}

/** Parse a GenerationSlot.id (string, possibly a temp id) into a real numeric
 * generation id, or null when it isn't a finite positive integer. In-flight
 * slots carry non-numeric temp ids and must NOT be markable. */
export function parseGenerationId(id: string | number | null | undefined): number | null {
  if (id === null || id === undefined) return null;
  const n = typeof id === 'number' ? id : Number(id);
  if (!Number.isFinite(n) || n <= 0 || !Number.isInteger(n)) return null;
  return n;
}

/** Resolve a slot's real DB generation id for marking. Two slot shapes exist:
 * gallery slots carry the numeric generation id directly in `id`, while session
 * workbench slots carry a temp string `id` (`slot_<ts>_<rand>`) and keep the real
 * id in `asyncGenerationId` (set on completion for both sync and async paths).
 * Returns null when neither yields a valid id (e.g. still in-flight). */
export function resolveSlotGenerationId(
  slotId: string | number | null | undefined,
  asyncGenerationId?: number | null
): number | null {
  return parseGenerationId(slotId) ?? parseGenerationId(asyncGenerationId);
}
